if (!customElements.get("product-modal")) {
  customElements.define(
    "product-modal",
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.currentIndex = 0;
        this.allowClose = false; // The "safety latch"
        this.isMobile = window.matchMedia("(max-width: 749px)").matches;
        // Double tap tracking
        this.lastTapTime = 0;
        this.lastTapTarget = null;
        this.doubleTapDelay = 300; // Milliseconds
        this.scrollTimeout = null; // For debouncing scroll events
        // --- NEW: Zoom container management ---
        this.zoomContainer = null;
        // --- NEW: Add a dedicated click handler for the zoom container ---

        this.onZoomContainerClick = (event) => {
          this.toggleZoom(this.media[this.currentIndex]);
        };
        // --- END NEW ---

        // --- Bind the slide's touchmove handler ---
        this.onSlideTouchMove = this.handleSlideTouchMove.bind(this);
        // --- END NEW ---

        // Master click listener
        this.masterClickListener = (event) => {
          const target = event.target;
          const closeButton = this.querySelector(
            ".product-media-modal__toggle"
          );
          const prevButton = this.querySelector(".slider-button--prev");
          const nextButton = this.querySelector(".slider-button--next");
          // --- [START NEW] ---
          const zoomInButton = this.querySelector(
            ".product-media-modal__zoom-button--in"
          );
          const zoomOutButton = this.querySelector(
            ".product-media-modal__zoom-button--out"
          );
          // --- [END NEW] ---
          const activeMedia = this.media ? this.media[this.currentIndex] : null;

          // Case 1: Close button
          if (
            closeButton &&
            (target === closeButton || closeButton.contains(target))
          ) {
            this.allowClose = true;
            return;
          }

          // Case 2: Prev button
          if (
            prevButton &&
            this.contains(prevButton) &&
            (target === prevButton || prevButton.contains(target))
          ) {
            event.stopPropagation();
            event.preventDefault();
            if (event.type === "click") this.onPrevButtonClick();
            return;
          }

          // Case 3: Next button
          if (
            nextButton &&
            this.contains(nextButton) &&
            (target === nextButton || nextButton.contains(target))
          ) {
            event.stopPropagation();
            event.preventDefault();
            if (event.type === "click") this.onNextButtonClick();
            return;
          }

          // --- [START NEW] ---
          // Case 4: Zoom In Button
          if (
            zoomInButton &&
            this.contains(zoomInButton) &&
            (target === zoomInButton || zoomInButton.contains(target))
          ) {
            event.stopPropagation();
            event.preventDefault();
            if (event.type === "click" && activeMedia) {
              // Get center coordinates for the zoom
              const rect = activeMedia.getBoundingClientRect();
              const x = rect.width / 2;
              const y = rect.height / 2;
              this.toggleZoom(activeMedia, { x, y });
            }
            return;
          }

          // Case 5: Zoom Out Button
          if (
            zoomOutButton &&
            this.contains(zoomOutButton) &&
            (target === zoomOutButton || zoomOutButton.contains(target))
          ) {
            event.stopPropagation();
            event.preventDefault();
            if (event.type === "click" && activeMedia) {
              this.toggleZoom(activeMedia); // No coordinates needed to zoom out
            }
            return;
          }
          // --- [END NEW] ---

          // Case 4: Active media slide (Zoom - Double Tap for Mobile)
          if (activeMedia && activeMedia.contains(target)) {
            event.stopPropagation();
            event.preventDefault();
            if (event.type === "click") {
              if (this.isMobile) {
                // Double Tap Logic
                const currentTime = Date.now();
                if (
                  target === this.lastTapTarget &&
                  currentTime - this.lastTapTime < this.doubleTapDelay
                ) {
                  // --- MODIFICATION: Capture tap coordinates ---
                  const rect = activeMedia.getBoundingClientRect();
                  const x = event.clientX - rect.left;
                  const y = event.clientY - rect.top;
                  this.toggleZoom(activeMedia, { x, y });
                  // --- END MODIFICATION ---
                  this.lastTapTime = 0;
                  this.lastTapTarget = null;
                } else {
                  this.lastTapTime = currentTime;
                  this.lastTapTarget = target;
                }
              } else {
                // Desktop: Single tap zoom
                this.toggleZoom(activeMedia); // Use helper function
              }
            }
            return;
          }

          // Case 5: Backdrop click
          if (!this.allowClose) {
            event.stopPropagation();
            event.preventDefault();
          }
        };

        // --- Scroll handler ---
        this.debouncedScrollHandler = this.debounce(() => {
          // --- NEW: Guard clause ---
          // If zoomed, do not process scroll events.
          if (this.classList.contains("is-zoomed")) {
            return;
          }
          // --- END NEW ---

          if (!this.content || !this.media || this.media.length === 0) return;

          const slideWidth = this.content.clientWidth; // Width of the viewport (one slide)
          const currentScroll = this.content.scrollLeft;
          const newIndex = Math.round(currentScroll / slideWidth);

          // Update state only if index actually changed
          if (newIndex !== this.currentIndex && newIndex < this.media.length) {
            console.log(`Scroll detected new index: ${newIndex}`);
            this.updateSlideState(newIndex); // Update counter, buttons, active class
          }
        }, 150); // Debounce timeout in milliseconds
      } // End constructor

      // --- Handle touch gestures on the SLIDE itself ---
      handleSlideTouchMove(event) {
        if (this.classList.contains("is-zoomed")) {
          // If the modal is zoomed, stop this touch event
          // from bubbling up to the container.
          // This allows the slide's internal scroll (pan)
          // to work without triggering the container's scroll.
          event.stopPropagation();
        }
      }
      // --- END NEW ---

      // Helper function to toggle zoom and body classes
      // This function should NOT change any styles directly
      toggleZoom(mediaElement, coordinates = null) {
        const isNowZoomingIn = !this.classList.contains("is-zoomed");

        if (isNowZoomingIn) {
          // --- ZOOM IN ---
          this.classList.add("is-zoomed"); // Add class to modal
          document.body.classList.add("overflow-hidden-zoom");
          document.documentElement.classList.add("overflow-hidden-zoom");

          if (coordinates && this.isMobile) {
            // --- NEW: Create a dedicated zoom container for mobile ---
            const imageElement =
              mediaElement.querySelector("img") || mediaElement;
            if (!imageElement) return;

            this.zoomedImage = imageElement;
            this.originalParent = imageElement.parentElement;
            this.originalNextSibling = imageElement.nextSibling;

            this.zoomContainer = document.createElement("div");
            this.zoomContainer.className =
              "product-media-modal__zoom-container";

            // Move the image into the new container
            this.zoomContainer.appendChild(imageElement);
            this.zoomContainer.addEventListener(
              "click",
              this.onZoomContainerClick
            ); // Add listener
            this.appendChild(this.zoomContainer); // Append to the modal itself

            // Use a short timeout to allow the element to be added to the DOM before scaling
            // This ensures the CSS transition is applied correctly.
            setTimeout(() => {
              this.zoomContainer.classList.add("zoomed");
              // --- [FIX] ---
              // Manually set the container's scroll to the center.
              // This allows scrolling left/up.
              const scaledWidth = this.zoomedImage.offsetWidth * 2;
              const scaledHeight = this.zoomedImage.offsetHeight * 2;

              this.zoomContainer.scrollLeft =
                (scaledWidth - this.zoomContainer.clientWidth) / 2;
              this.zoomContainer.scrollTop =
                (scaledHeight - this.zoomContainer.clientHeight) / 2;
              // --- [END FIX] ---
            }, 10);

            // --- END NEW ---
          } else {
            // Desktop zoom logic (scale in place)
            mediaElement.classList.add("zoomed");
            if (coordinates) {
              // This might be useful for future desktop features
              mediaElement.style.setProperty(
                "--zoom-origin-x",
                `${coordinates.x}px`
              );
              mediaElement.style.setProperty(
                "--zoom-origin-y",
                `${coordinates.y}px`
              );
            }
          }
        } else {
          // --- ZOOM OUT ---
          this.classList.remove("is-zoomed"); // Remove class from modal
          document.body.classList.remove("overflow-hidden-zoom");
          document.documentElement.classList.remove("overflow-hidden-zoom");

          // --- NEW: Cleanup for mobile zoom container ---
          if (this.zoomContainer && this.originalParent && this.zoomedImage) {
            // Move the image back to its original position
            this.originalParent.insertBefore(
              this.zoomedImage,
              this.originalNextSibling
            );
            this.zoomContainer.removeEventListener(
              "click",
              this.onZoomContainerClick
            ); // Clean up listener
            this.zoomContainer.remove();
            this.zoomContainer = null;
            this.originalParent = null;
            this.originalNextSibling = null;
            this.zoomedImage = null;
          } else {
            // Desktop zoom out
            mediaElement.classList.remove("zoomed");
            mediaElement.style.removeProperty("--zoom-origin-x");
            mediaElement.style.removeProperty("--zoom-origin-y");
          }

          // --- [FIX] ---
          // Force the container to scroll to the correct slide.
          // This fixes any scroll "drift" that happened while zoomed.
          this.content.scrollTo({
            left: this.content.clientWidth * this.currentIndex,
            behavior: "auto",
          });
          // --- [END FIX] ---
        }
      }

      // Simple debounce function
      debounce(func, wait) {
        let timeout;
        return (...args) => {
          clearTimeout(timeout);
          timeout = setTimeout(() => func.apply(this, args), wait);
        };
      }

      hide() {
        if (this.allowClose === true) {
          // Remove listeners
          document.removeEventListener("click", this.masterClickListener, true);
          document.removeEventListener(
            "pointerdown",
            this.masterClickListener,
            true
          );
          if (this.content) {
            this.content.removeEventListener(
              "scroll",
              this.debouncedScrollHandler
            ); // Remove scroll listener
          }

          // --- [FIX] 3. This loop is incorrect, remove it ---
          /*
          if (this.media) {
            this.media.forEach((slide) => {
              slide.removeEventListener('touchmove', this.onSlideTouchMove);
            });
          }
          */

          this.allowClose = false;
          // Reset zoom/body classes
          if (this.media) {
            this.media.forEach((element) => element.classList.remove("zoomed"));
            // --- NEW: Ensure zoom container is removed on modal close ---
            if (this.zoomContainer) {
              this.zoomContainer.removeEventListener(
                "click",
                this.onZoomContainerClick
              ); // Cleanup
              this.zoomContainer.removeEventListener(
                "touchmove",
                this.onSlideTouchMove
              ); // --- [FIX] 4. ADD CLEANUP HERE
              this.zoomContainer.remove();
              this.originalParent.insertBefore(
                this.zoomedImage,
                this.originalNextSibling
              );
              this.zoomContainer = null;
            }
            // --- END NEW ---
            document.body.classList.remove("overflow-hidden-zoom");
            document.documentElement.classList.remove("overflow-hidden-zoom");
            this.classList.remove("is-zoomed"); // Ensure is-zoomed class is removed from the modal itself
          }
          // Reset tap tracking
          this.lastTapTime = 0;
          this.lastTapTarget = null;
          super.hide();
        }
      }

      show(opener) {
        this.allowClose = false;
        // Add listeners
        document.addEventListener("click", this.masterClickListener, true);
        document.addEventListener(
          "pointerdown",
          this.masterClickListener,
          true
        );

        super.show(opener);
        this.media = this.querySelectorAll("[data-media-id]");
        this.counter = this.querySelector(".product-media-modal__counter");
        this.content = this.querySelector(".product-media-modal__content");
        this.isMobile = window.matchMedia("(max-width: 749px)").matches;

        if (this.content) {
          clearTimeout(this.scrollTimeout);
          this.content.addEventListener("scroll", this.debouncedScrollHandler);
        }

        // --- [FIX] This loop was incorrect, it has been removed ---
        /*
        if (this.media) {
            this.media.forEach((slide) => {
                slide.addEventListener('touchmove', this.onSlideTouchMove);
            });
        }
        */

        this.showActiveMedia();
      }

      showActiveMedia() {
        if (!this.media || this.media.length === 0 || !this.content) return;
        const mediaId = this.openedBy.getAttribute("data-media-id");
        let newIndex = 0;
        this.media.forEach((element, index) => {
          if (element.getAttribute("data-media-id") === mediaId)
            newIndex = index;
        });
        // Go to initial slide instantly, update state
        this.content.scrollLeft = this.content.clientWidth * newIndex; // Set initial scroll without animation
        this.updateSlideState(newIndex); // Update counter/buttons/active class
        // Load initial deferred media
        this.loadDeferred(this.media[newIndex]);
      }

      // Renamed from goToSlide - Only handles button clicks now
      goToSlideByIndex(newIndex, isInitial = false) {
        if (!this.media || !this.content) return;
        if (newIndex < 0) newIndex = 0;
        if (newIndex >= this.media.length) newIndex = this.media.length - 1;

        const targetScrollLeft = this.content.clientWidth * newIndex;

        // Use smooth scroll for button clicks
        this.content.scrollTo({
          left: targetScrollLeft,
          behavior: "smooth",
        });
      }

      // --- Function to update counter, buttons, active class ---
      updateSlideState(newIndex) {
        if (!this.media || newIndex >= this.media.length || newIndex < 0)
          return;

        // --- NEW: Guard against state update while zoomed ---
        // This prevents the slide from changing underneath the zoom
        if (this.classList.contains("is-zoomed")) {
          // If we are zoomed, force the scrollbar back to the current slide
          const targetScrollLeft = this.content.clientWidth * this.currentIndex;
          this.content.scrollTo({ left: targetScrollLeft, behavior: "auto" });
          return;
        }
        // --- END NEW ---

        // Update counter
        if (this.counter) {
          const current = newIndex + 1;
          const total = this.media.length;
          this.counter.textContent = `${current} / ${total}`;
        }

        // Remove active/zoomed from previous slide
        const previousMedia = this.media[this.currentIndex];
        if (previousMedia && this.currentIndex !== newIndex) {
          previousMedia.classList.remove("active");
          previousMedia.classList.remove("zoomed");
          this.classList.remove("is-zoomed"); // Ensure modal is not marked as zoomed
          document.body.classList.remove("overflow-hidden-zoom");
          document.documentElement.classList.remove("overflow-hidden-zoom");
          previousMedia.scrollTop = 0;
          previousMedia.scrollLeft = 0;
        }

        // Add active to current slide
        const activeMedia = this.media[newIndex];
        if (activeMedia) {
          activeMedia.classList.add("active");
          if (activeMedia.classList.contains("zoomed")) {
            activeMedia.classList.remove("zoomed"); // Ensure zoom is off on slide change
            this.classList.remove("is-zoomed"); // Also remove from modal
            document.body.classList.remove("overflow-hidden-zoom");
            document.documentElement.classList.remove("overflow-hidden-zoom");
          }
          // Reset scroll for the new active slide
          activeMedia.scrollTop = 0;
          activeMedia.scrollLeft = 0;
        }

        this.currentIndex = newIndex; // Update index
        this.updateButtonStates(); // Update arrow states

        // Load deferred media for the new active slide
        this.loadDeferred(activeMedia);

        // Reset tap tracking on slide change
        this.lastTapTime = 0;
        this.lastTapTarget = null;
      }
      // --- End Update State function ---

      // --- Helper to load deferred media ---
      loadDeferred(mediaElement) {
        if (!mediaElement) return;
        const template = mediaElement.querySelector("template");
        const content = template ? template.content : null;
        if (
          mediaElement.nodeName === "DEFERRED-MEDIA" &&
          content &&
          content.querySelector(".js-youtube")
        ) {
          mediaElement.loadContent();
        }
      }
      // --- End Helper ---

      // Button clicks now call goToSlideByIndex
      onPrevButtonClick() {
        this.goToSlideByIndex(this.currentIndex - 1);
      }

      onNextButtonClick() {
        this.goToSlideByIndex(this.currentIndex + 1);
      }

      updateButtonStates() {
        const prev = this.querySelector(".slider-button--prev");
        const next = this.querySelector(".slider-button--next");
        if (!prev || !next) return;
        prev.disabled = this.currentIndex === 0;
        next.disabled =
          this.media && this.currentIndex === this.media.length - 1;
      }
    }
  );
}
